export type SplitDollarValues = {
  dollarSub: string;
  dollarSub2: string;
  dollarMaster: string;
};

type SplitDollarValuesInput = {
  split: number;
  split2: number;
  masterShare: number;
  isTwoSub: boolean;
};

const HYPOTHETICAL_REBATE = 1000;
const PLACEHOLDER = "$--";

const formatHypotheticalPayout = (pct: number): string =>
  `$${((Math.max(0, pct) * HYPOTHETICAL_REBATE) / 100).toLocaleString("en-US")}`;

/**
 * Whether the Master has entered a share for at least one Sub IB yet.
 *
 * Until they have, the step shows placeholders rather than derived numbers: the
 * Master's own share is only *implied* by the untouched inputs, and rendering
 * the implied 100% reads as a real allocation the user never made. A zero total
 * is not a submittable split either (the step's own Continue is disabled for
 * it), so zero means "nothing entered", not "nothing shared".
 *
 * Shared with the "You receive" percentage so the caption and the percentage
 * can never disagree about whether a value exists.
 */
export const hasEnteredSplit = ({
  split,
  split2,
  isTwoSub,
}: Pick<SplitDollarValuesInput, "split" | "split2" | "isTwoSub">): boolean =>
  isTwoSub ? split > 0 || split2 > 0 : split > 0;

/**
 * Formats the "on a hypothetical $1,000 in rebate" payout preview shown in
 * the Split step. Reads "$--" for every value until the user has entered a
 * share for at least one participant.
 */
export const computeSplitDollarValues = ({
  split,
  split2,
  masterShare,
  isTwoSub,
}: SplitDollarValuesInput): SplitDollarValues => {
  if (!hasEnteredSplit({ split, split2, isTwoSub })) {
    return {
      dollarSub: PLACEHOLDER,
      dollarSub2: PLACEHOLDER,
      dollarMaster: PLACEHOLDER,
    };
  }

  return {
    dollarSub: formatHypotheticalPayout(split),
    dollarSub2: formatHypotheticalPayout(split2),
    dollarMaster: formatHypotheticalPayout(masterShare),
  };
};
